import { useState } from 'react';

const SECTIONS = [
  {
    id: 'what-is-betting',
    emoji: '🎲',
    title: 'What is Sports Betting?',
    summary: 'A simple introduction to how sports betting works.',
    navigate: null,
    content: `
Sports betting is the activity of predicting the outcome of a sporting event and placing a wager on that prediction. If your prediction is correct, you win money based on the odds offered. If it is wrong, you lose your stake.

**How it works in practice:**
You visit a bookmaker (like Betway or Sunbet), choose a sporting event, pick the outcome you think will happen, decide how much to stake, and place your bet. The bookmaker pays you based on the odds if you are correct.

**What can you bet on?**
Almost any sporting event — football match results, total goals scored, basketball points, tennis set winners, cricket wickets, and many more. In Botswana, football is by far the most popular sport to bet on, followed by basketball, rugby, and cricket.

**Is sports betting gambling?**
Yes — sports betting is a form of gambling. Most bettors lose money over time because bookmakers build a profit margin into their odds. However, unlike casino games (roulette, slots), sports betting can be beaten by skilled bettors who consistently find value — odds that are higher than the true probability of an outcome.

**The golden rule:**
Only ever bet money you can afford to lose. Treat betting as entertainment, not as a source of income. Set a budget before you start and stick to it.
    `,
    example: {
      title: 'Simple example',
      text: 'Betway offers Chelsea to win at odds of 2.50. You bet P50 on Chelsea. If Chelsea wins, you receive P125 (P50 × 2.50) — a profit of P75. If Chelsea draws or loses, you lose your P50 stake.',
    },
  },
  {
    id: 'types-of-bets',
    emoji: '📋',
    title: 'Types of Bets',
    summary: 'Singles, accumulators, parlays and more — explained simply.',
    navigate: null,
    content: `
There are many different types of bets available, but these are the most common ones you will encounter at BW bookmakers.

**Single bet**
The simplest bet — one selection on one event. If your selection wins, you win. All serious bettors focus primarily on singles because the result depends only on one outcome.

**Accumulator (Parlay / Multi-bet)**
Combining multiple selections into one bet. All selections must win for you to collect. The odds multiply together, creating much larger potential payouts — but also much greater risk. For example, a 4-team accumulator at average odds of 2.00 per selection gives combined odds of 16.00. One loss and you lose everything.

**Double**
An accumulator with exactly 2 selections. Both must win.

**Treble**
An accumulator with exactly 3 selections. All three must win.

**System bet**
A more complex bet that covers multiple combinations within a group of selections. For example, a "Trixie" covers 3 selections in 4 bets (3 doubles + 1 treble) — you can still win even if one selection loses.

**Which should you use?**
Professional bettors focus on singles and use Kelly staking to size their bets correctly. Accumulators are exciting but statistically disadvantage you more — the bookmaker's margin multiplies with each leg. Our Smart Parlay Builder helps you build better accumulators using only value-positive selections.
    `,
    example: {
      title: 'Accumulator vs singles',
      text: 'You bet P20 on a 5-team accumulator at combined odds of 32.00 — potential return P640. But if you had staked P4 on each team as singles (same total P20), you could profit from each winner independently. Accumulators are more exciting but singles are more profitable over time.',
    },
  },
  {
    id: 'how-bookmakers-work',
    emoji: '🏦',
    title: 'How Bookmakers Work',
    summary: 'Why bookmakers always make money — and how you can still beat them.',
    navigate: null,
    content: `
Bookmakers are businesses designed to make a profit regardless of which team wins. Understanding how they operate is essential for any serious bettor.

**They set odds below fair value**
For every market, bookmakers set odds so the combined implied probability of all outcomes adds up to more than 100%. The excess is their margin — also called the vig, juice, or overround. This is how they guarantee profit over time regardless of results.

**They balance their books**
Bookmakers aim to take roughly equal money on all sides of a bet so they profit from the margin without taking on risk. When too much money comes in on one side, they adjust the odds to attract money on the other side.

**They limit winning accounts**
This is critical to understand: bookmakers do not want skilled bettors. If you consistently win, especially at large stakes, bookmakers will limit your account — reducing the maximum bet they will accept from you. This is legal and common. The best bettors in the world are limited at most bookmakers.

**How to avoid getting limited:**
- Use multiple bookmakers (all 6 on OddsEdgeBW)
- Vary your stake sizes — do not always bet the maximum
- Do not always take the best available price on every single market
- Place some recreational bets on popular markets occasionally
- Withdraw winnings regularly rather than letting them accumulate

**Sharp vs recreational bookmakers**
Some bookmakers (called "sharps") welcome winning bettors because they use sharp money to set better lines. Others (called "square" or recreational books) limit winners quickly. In Botswana, most bookmakers are recreational — they cater to casual bettors and will limit consistent winners.
    `,
    example: {
      title: 'Why you should use all 6 bookmakers',
      text: 'If you only bet with Betway and win consistently, your account will be limited within weeks. But if you spread your bets across all 6 BW bookmakers, you take longer to get limited at each one, and you always have alternatives available when one limits you.',
    },
  },
  {
    id: 'common-mistakes',
    emoji: '⚠️',
    title: 'Common Betting Mistakes',
    summary: 'The habits that cost most bettors money — and how to avoid them.',
    navigate: null,
    content: `
Most sports bettors lose money over time. These are the most common reasons why.

**1. Betting without value**
Placing bets simply because you think a team will win — without considering whether the odds reflect good value — is the most common mistake. A team can be likely to win but still be a bad bet if the odds are too low. Always ask: are these odds higher than the true probability?

**2. Chasing losses**
After a losing run, many bettors increase their stakes to try to recover losses quickly. This is one of the most dangerous habits in betting and leads to catastrophic losses. The next bet has no memory of the previous one. Stick to your staking plan regardless of recent results.

**3. Betting on your favourite team**
Emotional attachment to a team clouds your judgement. Research consistently shows bettors overestimate the chances of their favourite team and underestimate opponents. Bet with your head, not your heart.

**4. Too many accumulators**
Accumulators feel exciting and the potential payouts are attractive, but each leg multiplies the bookmaker's margin. A 5-leg accumulator at 5% margin per leg means the bookmaker has a 23% edge overall. Stick primarily to singles.

**5. Ignoring bankroll management**
Betting random amounts — P10 here, P200 there — without a consistent staking plan guarantees you will go broke during a normal losing run. Use Kelly staking or flat stakes as a percentage of your bankroll.

**6. Believing in "hot streaks" and "due" outcomes**
A team on a winning run is not "due" to lose, and a coin that has landed heads 5 times is not "due" to land tails. Each event is independent. Base decisions on value, not patterns.

**7. Not shopping for the best odds**
Consistently taking worse odds than the best available costs you money over hundreds of bets. This is exactly what OddsEdgeBW's Best Odds page solves — always get the best available price.
    `,
    example: {
      title: 'The cost of not shopping odds',
      text: 'Over 100 bets, consistently taking 1.90 instead of the best available 2.00 costs you approximately 5% of your total stakes. On P5,000 total staked, that is P250 left on the table simply by not checking which bookmaker offers the best price.',
    },
  },
  {
    id: 'odds',
    emoji: '🔢',
    title: 'How to Read Odds',
    summary: 'Understand what odds actually mean before placing any bet.',
    navigate: 'calculators',
    navigateLabel: 'Try Odds Converter →',
    content: `
Odds tell you two things: how much you can win, and how likely the bookmaker thinks an outcome is.

In Botswana, almost all bookmakers use **decimal odds** — the most straightforward format.

**How decimal odds work:**
Your stake × odds = total return (including your original stake)

**Example:**
You bet P100 on Sunbet at odds of 2.50.
- If you win: P100 × 2.50 = P250 total return (P150 profit)
- If you lose: you lose your P100 stake

The implied probability is simply: 1 ÷ odds × 100
- Odds of 2.50 → 1 ÷ 2.50 × 100 = **40% implied probability**
- Odds of 1.50 → 1 ÷ 1.50 × 100 = **66.7% implied probability**
- Odds of 4.00 → 1 ÷ 4.00 × 100 = **25% implied probability**

**The golden rule:** Higher odds = less likely to win but bigger payout. Lower odds = more likely to win but smaller payout.
    `,
    example: {
      title: 'Real example',
      text: 'Egypt vs Iran at Betway. Egypt win at 2.43, Draw at 2.60, Iran win at 3.65. If you bet P200 on Egypt and they win, you receive P486 (P286 profit). The implied probabilities are: Egypt 41.2%, Draw 38.5%, Iran 27.4% — totalling 107.1%, meaning the bookmaker has a 7.1% margin built in.',
    },
  },
  {
    id: 'margin',
    emoji: '📊',
    title: 'The Bookmaker Margin',
    summary: 'How bookmakers guarantee profit — and why it matters to you.',
    navigate: null,
    content: `
Bookmakers are not charities. They make money by offering odds that are slightly worse than the true probability of each outcome.

If you add up the implied probabilities of all outcomes in a match, a fair market would total exactly 100%. Bookmakers set their odds so the total is higher — typically 105% to 110%. The extra percentage is their guaranteed profit margin, called the **vig** or **juice**.

**Example:**
A coin flip should be 50/50. Fair odds would be 2.00 for heads and 2.00 for tails. But a bookmaker might offer 1.90/1.90:
- 1 ÷ 1.90 + 1 ÷ 1.90 = 52.6% + 52.6% = **105.3%**
- The 5.3% over 100% is their margin.

**Why this matters:**
If you randomly bet on sports, the margin slowly drains your bankroll. To profit long-term, you need to find bets where the odds are better than the true probability — this is called value betting.

**Lower margin = better for bettors.** OddsEdgeBW shows you the Book % for every match. Anything under 100% is a true arbitrage. Anything close to 100% is a competitive market.
    `,
    example: {
      title: 'BW bookmaker comparison',
      text: 'For the same match, BBets offers odds totalling 104.5% margin while Betway offers 106.8%. Betting on BBets gives you a 2.3% better starting position on every bet you place with them on that market.',
    },
  },
  {
    id: 'value',
    emoji: '📈',
    title: 'Value Betting',
    summary: 'The foundation of profitable betting — finding odds that are too high.',
    navigate: 'valuebets',
    navigateLabel: 'View Value Bets →',
    content: `
A value bet exists when a bookmaker's odds are higher than the true probability of an outcome. If you can consistently find and bet on value, you will profit in the long run — even if you lose individual bets.

**Think of it like this:**
Imagine flipping a coin that lands heads 60% of the time, but a bookmaker is offering odds of 2.20 on heads (implying only 45.5%). Every time you bet on heads, you have an edge. You will not win every flip, but over hundreds of bets, you will profit.

**How to spot value:**
1. Estimate the true probability of an outcome
2. Convert to fair odds: 1 ÷ probability
3. If the bookmaker's odds are higher than your fair odds, it is value

**The key mindset:**
Value bettors measure success over hundreds of bets, not individual results. A losing bet can still be a good bet if it had positive expected value. A winning bet can still be a bad bet if the odds were too low.
    `,
    example: {
      title: 'OddsEdgeBW Value Bets',
      text: 'Our Value Bets scanner compares odds across all 6 BW bookmakers. When one bookmaker offers significantly more than the consensus price, we flag it as a value bet with the edge percentage and Kelly stake recommendation.',
    },
  },
  {
    id: 'arb',
    emoji: '⚡',
    title: 'Arbitrage Betting',
    summary: 'Guaranteed profit by backing all outcomes at different bookmakers.',
    navigate: 'arbitrage',
    navigateLabel: 'View Arb Opportunities →',
    content: `
Arbitrage (arb) betting is when you back all possible outcomes of a match at different bookmakers, and the combined implied probability is under 100%. This creates a guaranteed profit regardless of the result.

**How it works:**
Different bookmakers have different opinions on the same match. When these opinions diverge enough, an opportunity appears.

**Step-by-step example:**
Match: LTU vs VG-62

- Back Home at PST Bets: odds 3.45
- Back Draw at Betway: odds 4.30
- Back Away at Betway: odds 4.90

Implied probabilities: 29.0% + 23.3% + 20.4% = **72.7% total**

Since 72.7% is under 100%, you have an arb with a +27.3% margin.

**Important warnings:**
- Act fast — arbs disappear quickly as bookmakers adjust
- Never bet more than you can afford to lose on one leg before placing the others
- Some bookmakers limit or ban accounts that only arb
    `,
    example: null,
  },
{
  id: 'middles',
  emoji: '🎯',
  title: 'Middle Betting',
  summary: 'Bet both sides of a total — win big if the score falls in the window.',
  navigate: 'middles',
  navigateLabel: 'View Middles →',
  content: `
Middle betting is when you bet Over X goals at one bookmaker and Under Y goals at another bookmaker, where Y is higher than X. If the match ends with a score that falls between those two lines, both bets win — giving you a significant profit. If the score falls outside the window, one bet wins and one loses, resulting in a small loss.

**Example:**
- Bet Over 2.5 goals at Betway @ 2.35
- Bet Under 3.5 goals at BBets @ 1.21
- Window: exactly 3 goals scored

If 3 goals are scored: both bets win — profit on both legs.
If any other number of goals: one bet wins, one loses — small net loss.

**Why middles are valuable:**
The potential upside when a middle hits is large — typically 3x to 10x the downside if it misses. This asymmetric risk/reward is what makes middles attractive to professional bettors.

**How OddsEdgeBW finds middles:**
Our scanner compares Over/Under lines across all 6 BW bookmakers. When Betway offers Under 3.5 and BBets offers Over 2.5 on the same match, a window of exactly 3 goals exists where both bets win. We calculate the exact profit if the middle hits and the loss if it misses for every opportunity.

**Key things to remember:**
- Place both legs simultaneously — odds can change quickly
- Middles are not guaranteed profit like arbitrage — they require the score to fall in the window
- The risk/reward ratio shown in the calculator tells you how many times your potential profit exceeds your potential loss
- Soccer matches end with 2-3 goals most commonly — Over 2.5/Under 3.5 middles hit more often than you might expect
  `,
  example: {
    title: 'Real example from OddsEdgeBW',
    text: 'Over 1.5 @ 1.43 (BBets) + Under 2.5 @ 1.60 (Betway) on P200 total stake. If exactly 2 goals scored: win both for +P103 profit. If any other score: lose approximately P40. Risk/reward ratio: 2.6x.',
  },
},
{
  id: 'sharpMoney',
  emoji: '🔍',
  title: 'Sharp Money & Steam Moves',
  summary: 'Follow the smart bettors — track significant odds movements across bookmakers.',
  navigate: 'sharpMoney',
  navigateLabel: 'View Sharp Money →',
  content: `
Sharp money refers to bets placed by professional, winning bettors — often called "sharps". When sharps bet heavily on an outcome, bookmakers react by rapidly cutting their odds to limit their exposure. This rapid odds movement is called a "steam move".

**STEAM vs DRIFT:**
- **STEAM** — odds shortening fast. Money is coming IN on this outcome. Bookmakers are cutting prices because sharp customers are backing it heavily
- **DRIFT** — odds lengthening. Money is going elsewhere, or bookmakers are adjusting their position away from this outcome

**Why follow sharp money?**
Bookmakers trust their sharp customers more than their algorithms. When a sharp bettor places a large bet, it's because they have identified genuine value. By tracking these movements, you can identify the same opportunities the professionals are exploiting — often before the odds fully adjust.

**How to use it:**
- Look for STEAM moves of 10%+ on upcoming matches — these are the strongest signals
- When one bookmaker steams but others haven't moved yet, you can get the better price at the slower bookmaker
- Multiple bookmakers steaming on the same outcome simultaneously is the strongest signal of all
- Combine with value bets — if our value bet scanner and sharp money both agree, that's a very strong signal

**What causes DRIFT:**
- Public money backing the favourite (bookmakers welcome this and may even lengthen the price)
- Negative team news (injury, suspension) emerging
- Bookmakers repositioning their book

**Important caveat:**
Sharp money signals are indicators, not guarantees. Even professionals lose individual bets. Use sharp money as one input alongside value analysis, not as a standalone betting system.
  `,
  example: {
    title: 'Real example from OddsEdgeBW',
    text: 'PST Bets cut Talleres (away) from 10.00 → 5.60 (-44%) in 24 hours while BTTS Yes moved from 3.55 → 2.60 (-27%). This suggests sharp money backing Talleres to win and both teams to score — a specific, correlated prediction from professional bettors.',
  },
},
  {
    id: 'ev',
    emoji: '🧮',
    title: 'Expected Value (EV)',
    summary: 'The mathematical measure of whether a bet is worth taking.',
    navigate: 'calculators',
    navigateLabel: 'Try EV Calculator →',
    content: `
Expected Value (EV) is the average amount you expect to win or lose per bet if you placed the same bet thousands of times. It is the single most important concept in profitable betting.

**The formula:**
EV = (Probability of winning × Profit per win) − (Probability of losing × Stake)

**Positive EV (+EV):** The bet is in your favour. Place it consistently and you will profit long-term.
**Negative EV (−EV):** The bet is in the bookmaker's favour. Avoid.

**Example:**
You bet P100 on a team at odds of 3.00. You estimate their true win probability is 40%.

EV = (0.40 × P200 profit) − (0.60 × P100 stake)
EV = P80 − P60 = **+P20**

This means on average, each time you place this bet you expect to earn P20. Over 100 bets you would expect roughly P2,000 profit — even though you would lose 60% of individual bets.
    `,
    example: {
      title: 'Quick check',
      text: 'Before placing any bet, ask: "Do I believe the true probability is higher than the implied probability in these odds?" If yes, it is potentially +EV. If no, or you are not sure, do not bet.',
    },
  },
  {
    id: 'kelly',
    emoji: '📐',
    title: 'Kelly Criterion & Stake Sizing',
    summary: 'How much of your bankroll to risk on each bet.',
    navigate: 'calculators',
    navigateLabel: 'Try Kelly Calculator →',
    content: `
The Kelly Criterion is a mathematical formula that tells you the optimal percentage of your bankroll to stake on a bet. Bet too little and you grow slowly. Bet too much and you risk ruin.

**The formula:**
Kelly % = (Edge) ÷ (Odds − 1)

Where Edge = (Probability × Odds) − 1

**Example:**
- Odds: 2.50
- Your estimated win probability: 50%
- Edge = (0.50 × 2.50) − 1 = 0.25 (25% edge)
- Kelly % = 0.25 ÷ (2.50 − 1) = 0.25 ÷ 1.50 = **16.7% of bankroll**

If your bankroll is P1,000, Kelly recommends staking P167.

**Half Kelly (recommended):**
Most professional bettors use Half Kelly — staking half the recommended amount. This reduces variance significantly while still growing the bankroll efficiently.
    `,
    example: {
      title: 'OddsEdgeBW Kelly recommendations',
      text: 'Every value bet on our platform shows Full Kelly, Half Kelly, and Quarter Kelly stake recommendations based on the detected edge. We recommend Half Kelly as the default for most bettors.',
    },
  },
  {
    id: 'clv',
    emoji: '📉',
    title: 'Closing Line Value (CLV)',
    summary: 'The professional benchmark for measuring betting skill.',
    navigate: 'clv',
    navigateLabel: 'Track Your CLV →',
    content: `
The closing line is the final odds a bookmaker offers just before a match kicks off. It represents the most accurate assessment of probabilities because it incorporates all available information — team news, sharp money, public betting patterns, and market wisdom.

If you consistently get better odds than the closing line, you are demonstrating genuine skill. If you consistently get worse odds, you are likely making poor decisions regardless of your win rate.

**Why CLV matters:**
Win rate alone is misleading because of variance. CLV cuts through this noise and tells you whether you are genuinely finding value.

**Positive CLV example:**
You bet on Betway at odds of 2.40 on Monday. By Friday when the match starts, the closing odds are 2.10. You beat the close by 14.3% — this is strong positive CLV and indicates you had genuine edge at the time of betting.

**The professional standard:**
Sharp bettors are identified not by their win rate but by their CLV. Bookmakers limit and ban bettors with consistent positive CLV because it proves they are finding genuine value.
    `,
    example: {
      title: 'Track yours on OddsEdgeBW',
      text: 'Log your bets via the CLV Tracker. Our system automatically records closing odds for every match you bet on and calculates your CLV over time — the same metric professional betting syndicates use to evaluate their traders.',
    },
  },
  {
    id: 'bankroll',
    emoji: '💰',
    title: 'Bankroll Management',
    summary: 'Protect your money and survive the losing runs.',
    navigate: 'bankroll',
    navigateLabel: 'Manage Your Bankroll →',
    content: `
Even the best bettors in the world go through losing runs. Bankroll management is what separates bettors who survive and thrive from those who bust out during normal variance.

**The golden rules:**

1. **Never bet money you cannot afford to lose.** Your betting bankroll should be separate from living expenses.

2. **Start with a defined bankroll.** Decide on a starting amount. Every stake is a percentage of this, not a random amount.

3. **Never stake more than 5% on a single bet.** Even confident bets can lose. Larger stakes mean a losing run can wipe you out before your edge has time to play out.

4. **Track every bet.** You cannot improve what you do not measure. Record the bet, odds, stake, and result.

5. **Do not chase losses.** Increasing stake sizes after a losing run is the fastest way to ruin. The next bet has no memory of the previous one.

**Understanding variance:**
Even a bettor with a genuine 5% edge will have losing runs of 10–15 bets in a row. This is normal. With proper bankroll management, a losing run is a temporary setback. Without it, the same run ends your betting.
    `,
    example: {
      title: 'Example starting setup',
      text: 'Starting bankroll: P2,000. Max single bet: P100 (5%). Using Half Kelly on value bets averaging 8% edge at average odds of 2.20. Expected monthly growth: approximately 4–8% in a good month, with potential losing months of 5–10%.',
    },
  },
];

function AccordionItem({ section, isOpen, onToggle, onNavigate }) {
  return (
    <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '18px 24px',
          background: isOpen ? 'rgba(0,229,176,0.04)' : 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left',
          borderBottom: isOpen ? '1px solid var(--border)' : 'none',
          transition: 'background .15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 24 }}>{section.emoji}</span>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>
              {section.title}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{section.summary}</div>
          </div>
        </div>
        <span style={{ fontSize: 18, color: 'var(--text3)', flexShrink: 0, marginLeft: 12, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
          ↓
        </span>
      </button>

      {isOpen && (
        <div style={{ padding: '20px 24px' }}>
          {section.content.trim().split('\n\n').map((para, i) => {
            const processed = para.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={j} style={{ color: 'var(--text)', fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
              }
              return part;
            });

            if (para.startsWith('- ')) {
              return (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>{processed}</div>
                </div>
              );
            }

            if (para.match(/^\*\*\d+\./)) {
              return (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>{processed}</div>
                </div>
              );
            }

            return (
              <p key={i} style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.8, marginBottom: 12 }}>
                {processed}
              </p>
            );
          })}

          {section.example && (
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(61,142,248,0.06)', border: '1px solid rgba(61,142,248,0.15)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: 11, color: 'var(--accent2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                💡 {section.example.title}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>{section.example.text}</div>
            </div>
          )}

          {section.navigate && (
            <button
              onClick={() => onNavigate(section.navigate)}
              className="btn btn-primary"
              style={{ marginTop: 16, fontSize: 13, padding: '10px 20px' }}
            >
              {section.navigateLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Education({ onNavigate }) {
  const [openId, setOpenId] = useState('what-is-betting');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Header */}
      <div style={{ padding: '4px 0 16px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, marginBottom: 8 }}>
          Betting Intelligence Guide
        </div>
        <div style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1.6, maxWidth: 640 }}>
          Everything you need to bet smarter — from the absolute basics to advanced concepts like CLV and Kelly staking. Each section links to the relevant tool so you can apply what you learn immediately.
        </div>
      </div>

      {/* Quick links */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setOpenId(openId === s.id ? null : s.id)}
            style={{
              fontSize: 11, padding: '5px 12px', borderRadius: 20,
              border: `1px solid ${openId === s.id ? 'var(--accent)' : 'var(--border)'}`,
              background: openId === s.id ? 'rgba(0,229,176,0.1)' : 'transparent',
              color: openId === s.id ? 'var(--accent)' : 'var(--text3)',
              cursor: 'pointer', transition: 'all .15s',
            }}
          >
            {s.emoji} {s.title}
          </button>
        ))}
      </div>

      {/* Accordion sections */}
      {SECTIONS.map(section => (
        <AccordionItem
          key={section.id}
          section={section}
          isOpen={openId === section.id}
          onToggle={() => setOpenId(openId === section.id ? null : section.id)}
          onNavigate={onNavigate}
        />
      ))}

      {/* Footer note */}
      <div style={{ padding: '16px 20px', background: 'rgba(0,229,176,0.04)', border: '1px solid rgba(0,229,176,0.15)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)', lineHeight: 1.7 }}>
        ⚠️ <strong style={{ color: 'var(--text2)' }}>Responsible betting:</strong> Betting should be entertainment, not income. Never bet money you cannot afford to lose. If betting is causing financial stress, contact the National Responsible Gambling Programme on <strong style={{ color: 'var(--text2)' }}>0800 006 008</strong> (free, confidential).
      </div>
    </div>
  );
}